import { useEffect, useMemo, useRef, useState } from 'react';
import { activeWordAt, isFillerToken, type TranscriptWord } from '@captionflow/caption-engine';
import { useEditor } from '../state/editorStore';
import { api } from '../api/client';
import { playbackClock } from '../playback/clock';
import { SentenceView } from './SentenceView';

type Mode = 'sentences' | 'words';

/** Transcript-first editing surface: edit words, mark emphasis, hide fillers. */
export function TranscriptPanel() {
  const words = useEditor((s) => s.words);
  const selectedWordId = useEditor((s) => s.selectedWordId);
  const selectWord = useEditor((s) => s.selectWord);
  const editWordText = useEditor((s) => s.editWordText);
  const toggleEmphasis = useEditor((s) => s.toggleEmphasis);
  const toggleHidden = useEditor((s) => s.toggleHidden);
  const projectId = useEditor((s) => s.projectId);
  const language = useEditor((s) => s.language);
  const applyEnhancement = useEditor((s) => s.applyEnhancement);

  const [mode, setMode] = useState<Mode>('sentences');
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [enhancing, setEnhancing] = useState(false);
  const [enhanceNote, setEnhanceNote] = useState<string | null>(null);
  const [retranscribing, setRetranscribing] = useState(false);

  const runRetranscribe = async () => {
    if (!projectId || retranscribing) return;
    if (
      !window.confirm(
        'Re-transcribe this video from the source audio? This replaces the current transcript, breaks, and emphasis.',
      )
    )
      return;
    setRetranscribing(true);
    setEnhanceNote('Re-transcribing… this can take a minute for longer clips.');
    // Signature of the current transcript so we can detect the NEW one arriving.
    const before = `${words.length}|${words[0]?.text ?? ''}`;
    try {
      await api.retranscribe(projectId);
      const started = Date.now();
      // Poll until a fresh completed transcript appears (or time out ~4 min).
      for (;;) {
        await new Promise((r) => setTimeout(r, 3000));
        const t = await api.getTranscript(projectId);
        const sig = `${t.words.length}|${t.words[0]?.text ?? ''}`;
        const done = t.status === 'completed' && sig !== before && t.words.length > 0;
        if (done || Date.now() - started > 240_000) {
          if (t.words.length > 0) {
            useEditor.getState().load({
              words: t.words.map((w) => ({
                id: w.id,
                text: w.text,
                startMs: w.startMs,
                endMs: w.endMs,
                confidence: w.confidence,
                emphasized: w.emphasized,
                hidden: w.hidden,
                isFiller: w.isFiller,
              })),
              templateId: useEditor.getState().templateId, // keep the chosen template
              durationMs: t.durationMs ?? 0,
              videoUrl: t.sourceUrl,
            });
            useEditor.setState({ language: t.language });
          }
          setEnhanceNote(done ? 'Re-transcribed. Review the new transcript.' : 'Reloaded latest transcript.');
          break;
        }
      }
    } catch {
      setEnhanceNote("Re-transcribe failed - the video may still be processing. Try again shortly.");
    } finally {
      setRetranscribing(false);
    }
  };

  const runEnhance = async () => {
    if (!projectId || enhancing) return;
    const visible = words.filter((w) => !w.hidden);
    if (visible.length === 0) return;
    setEnhancing(true);
    setEnhanceNote(null);
    try {
      const res = await api.enhance(
        projectId,
        visible.map((w) => ({ id: w.id, text: w.text, startMs: w.startMs, endMs: w.endMs })),
        language,
      );
      applyEnhancement(res.segments);
      setEnhanceNote(
        res.source === 'ai'
          ? `AI set ${res.segments.length} caption groups & key words.`
          : `Enhanced with built-in rules (AI unavailable) - ${res.segments.length} groups.`,
      );
    } catch {
      setEnhanceNote("Couldn't reach the AI service. Try again.");
    } finally {
      setEnhancing(false);
    }
  };

  // Throttle active-word tracking to ~10fps for the transcript highlight (the
  // 60fps redraw happens on the canvas, not here).
  useEffect(() => {
    let last = 0;
    return playbackClock.subscribe((t) => {
      const now = performance.now();
      if (now - last < 100) return;
      last = now;
      const w = activeWordAt(words, t);
      setActiveId(w?.id ?? null);
    });
  }, [words]);

  const filtered = useMemo(() => {
    if (!query.trim()) return words;
    const q = query.toLowerCase();
    return words.filter((w) => w.text.toLowerCase().includes(q));
  }, [words, query]);

  return (
    <div className="transcript-panel">
      <div className="panel-header">
        <h2>Transcript</h2>
        <div className="seg-toggle">
          <button className={mode === 'sentences' ? 'on' : ''} onClick={() => setMode('sentences')}>
            Timestamps
          </button>
          <button className={mode === 'words' ? 'on' : ''} onClick={() => setMode('words')}>
            Words
          </button>
        </div>
      </div>

      <div className="enhance-bar">
        <button
          className="enhance-btn"
          onClick={runEnhance}
          disabled={enhancing || retranscribing || !projectId || words.length === 0}
          title={
            projectId
              ? 'Let AI pick the caption breaks and the key word in each group'
              : 'Open a saved project to use AI enhance'
          }
        >
          <span className="ai-badge">AI</span>
          {enhancing ? 'Enhancing…' : 'AI Enhance'}
        </button>
        <button
          className="retranscribe-btn"
          onClick={runRetranscribe}
          disabled={retranscribing || enhancing || !projectId}
          title={
            projectId
              ? 'Transcribe the video again from its audio (replaces the current transcript)'
              : 'Open a saved project to re-transcribe'
          }
        >
          {retranscribing ? 'Re-transcribing…' : 'Re-transcribe'}
        </button>
        {enhanceNote && <span className="enhance-note">{enhanceNote}</span>}
      </div>

      {mode === 'sentences' ? (
        <SentenceView />
      ) : (
        <>
          <input
            className="search"
            placeholder="Search words…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search transcript"
          />
          <p className="hint">
            Double-click a word to edit (Enter commits) - timing is preserved. e = emphasize, h =
            hide.
          </p>
          <div className="transcript-flow" role="list">
            {filtered.map((w) => (
              <WordChip
                key={w.id}
                word={w}
                selected={w.id === selectedWordId}
                active={w.id === activeId}
                onSelect={() => {
                  selectWord(w.id);
                  playbackClock.seek(w.startMs);
                }}
                onCommit={(text) => editWordText(w.id, text)}
                onEmphasis={() => toggleEmphasis(w.id)}
                onHide={() => toggleHidden(w.id)}
              />
            ))}
          </div>
          {selectedWordId && (
            <div className="word-actions">
              <button onClick={() => toggleEmphasis(selectedWordId)}>Emphasize</button>
              <button onClick={() => toggleHidden(selectedWordId)}>Hide/Show</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function WordChip({
  word,
  selected,
  active,
  onSelect,
  onCommit,
  onEmphasis,
  onHide,
}: {
  word: TranscriptWord;
  selected: boolean;
  active: boolean;
  onSelect: () => void;
  onCommit: (text: string) => void;
  onEmphasis: () => void;
  onHide: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(word.text);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) ref.current?.select();
  }, [editing]);

  const cls = [
    'word-chip',
    selected && 'selected',
    active && 'active',
    word.emphasized && 'emphasized',
    word.hidden && 'hidden',
    word.isFiller || isFillerToken(word.text) ? 'filler' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (editing) {
    return (
      <input
        ref={ref}
        className="word-edit"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          if (draft !== word.text) onCommit(draft);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setEditing(false);
            if (draft !== word.text) onCommit(draft);
          } else if (e.key === 'Escape') {
            setDraft(word.text);
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <span
      role="listitem"
      tabIndex={0}
      className={cls}
      onClick={onSelect}
      onDoubleClick={() => {
        setDraft(word.text);
        setEditing(true);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          setDraft(word.text);
          setEditing(true);
        } else if (e.key === 'e') onEmphasis();
        else if (e.key === 'h') onHide();
      }}
      title="Double-click to edit · e emphasize · h hide"
    >
      {word.text}
    </span>
  );
}
